import { TypedDocumentNode, gql } from '@apollo/client';
import { faArrowsRepeat } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonProps } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  MobileAppFeature,
  NotMobileAppFeature,
} from '@sorare/core/src/components/BuyableFeature';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import { mobileApp } from '@sorare/core/src/lib/glossary';

import { NewOfferBuilder } from 'components/directOffer/NewOfferBuilder';
import { useClickTradeEvent } from 'hooks/events/useClickTradeEvent';
import { useTradeButtonVisible } from 'hooks/offers/useTradeButtonVisible';

import { TradeButton_publicUserInfoInterface } from './__generated__/index.graphql';

type Props = {
  user: TradeButton_publicUserInfoInterface;
  renderButton?: (buttonProps: ButtonProps) => React.JSX.Element;
};

export const TradeButton = ({ user, renderButton }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const isTradeButtonVisible = useTradeButtonVisible(user.slug);
  const [offerBuilderOpen, toggleOfferBuilderOpen] = useToggle(false);

  const trackClickTrade = useClickTradeEvent();

  const openOfferBuilder = () => {
    toggleOfferBuilderOpen();
    trackClickTrade();
  };

  if (!isTradeButtonVisible) {
    return null;
  }

  return (
    <>
      <NotMobileAppFeature>
        {renderButton?.({ onClick: openOfferBuilder }) || (
          <Button size="medium" color="primary" onClick={openOfferBuilder}>
            <FontAwesomeIcon icon={faArrowsRepeat} />
            <span>
              <FormattedMessage
                id="USSportGalleryHeader.trade"
                defaultMessage="Trade"
              />
            </span>
          </Button>
        )}
        {offerBuilderOpen && currentUser && (
          <NewOfferBuilder
            onClose={toggleOfferBuilderOpen}
            to={user}
            currentUser={currentUser}
          />
        )}
      </NotMobileAppFeature>
      <MobileAppFeature>
        <Button size="medium" color="primary" onClick={() => {}} disabled>
          <FontAwesomeIcon icon={faArrowsRepeat} />
          <span>
            <FormattedMessage {...mobileApp.disabledTrade} />
          </span>
        </Button>
      </MobileAppFeature>
    </>
  );
};

TradeButton.fragments = {
  user: gql`
    fragment TradeButton_publicUserInfoInterface on PublicUserInfoInterface {
      id
      slug
      ...NewOfferBuilder_publicUserInfoInterface
    }
    ${NewOfferBuilder.fragments.user}
  ` as TypedDocumentNode<TradeButton_publicUserInfoInterface>,
};
